import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "../../lib/api";

async function uploadFile(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

export default function ImageUpload({ value, onChange, multiple = false, testid = "image-upload", aspect = "aspect-square" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = multiple ? value || [] : value ? [value] : [];

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      onChange(multiple ? [...list, ...paths] : paths[0]);
      toast.success(paths.length > 1 ? `${paths.length} imagens enviadas` : "Imagem enviada");
    } catch (err) {
      toast.error(errMsg(err, "Falha no envio da imagem."));
    } finally {
      setBusy(false);
    }
  };

  const remove = (i) => onChange(multiple ? list.filter((_, j) => j !== i) : "");
  const move = (i, d) => {
    const next = [...list];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };

  return (
    <div className="flex flex-wrap gap-3" data-testid={testid}>
      {list.map((p, i) => (
        <div key={p} className={`group relative w-28 overflow-hidden rounded-xl border border-white/10 bg-black ${aspect}`}>
          <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
          {multiple && i === 0 && <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-[#D4AF37] px-2 py-0.5 text-[9px] font-bold text-black"><Star className="h-2.5 w-2.5" />PRINCIPAL</span>}
          <button type="button" onClick={() => remove(i)} className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-500" aria-label="Remover imagem" data-testid={`${testid}-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
          {multiple && list.length > 1 && (
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1 opacity-0 transition-opacity group-hover:opacity-100">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="text-white disabled:opacity-30" aria-label="Mover para esquerda"><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} className="text-white disabled:opacity-30" aria-label="Mover para direita"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      ))}
      {(multiple || list.length === 0) && (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className={`flex w-28 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4AF37]/40 text-xs text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/5 ${aspect}`} data-testid={`${testid}-button`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          {busy ? "Enviando…" : "Enviar imagem"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple={multiple} className="hidden" onChange={handle} data-testid={`${testid}-input`} />
    </div>
  );
}
